import { useMemo, useState } from 'react';
import { GeoJsonLayer } from '@deck.gl/layers';
import { feature, featureCollection } from '@turf/turf';

import { Building, RealEstateAssetSummary } from './buildings.dto';

// Single color used for every building that belongs to an existing real estate asset.
// We deliberately do NOT vary by REA identity — the user just needs to know "this
// building is part of some REA" at a glance; the sidebar conveys which one.
const REA_COLOR: [number, number, number, number] = [0, 200, 0, 140];

export function BuildingsDeckLayer(
    buildings: Building[],
    onSelectionChanged?: (selected: Building[]) => void,
    externalSelection?: Building[],
    onBuildingClicked?: (building: Building) => void,
    realEstateAssets?: RealEstateAssetSummary[],
    hoveredBuildingId?: string | null,
    onHoverChanged?: (buildingId: string | null) => void,
    tooltipOverrideMap?: Map<string, string>,
) {
    const [internalSelected, setInternalSelected] = useState<Building[]>([]);

    const selectedBuildings = externalSelection ?? internalSelected;

    const buildingIdsInAnyRea = useMemo(() => {
        const set = new Set<string>();
        if (realEstateAssets) {
            for (const rea of realEstateAssets) {
                for (const b of rea.buildings) set.add(b.id);
            }
        }
        return set;
    }, [realEstateAssets]);

    const geoJsonFeatures = useMemo(
        () =>
            featureCollection(
                buildings
                    .filter((building) => building.geometry != null)
                    .map((building) =>
                        feature(building.geometry, {
                            id: building.id,
                            tooltip:
                                tooltipOverrideMap?.get(building.id) ??
                                `${building.buildingFunction?.name ?? ''} | ${building.name ?? ''} (${building.code})`,
                        }),
                    ),
            ),
        [buildings, tooltipOverrideMap],
    );

    function isBuildingSelected(buildingId: string): boolean {
        return selectedBuildings.some((building) => building.id === buildingId);
    }

    function getFillColor(buildingId: string): [number, number, number, number] {
        const base: [number, number, number, number] = (() => {
            if (isBuildingSelected(buildingId)) return [255, 0, 0, 100];
            if (buildingIdsInAnyRea.has(buildingId)) return REA_COLOR;
            return [255, 255, 0, 100];
        })();
        // Fully opaque on hover so the building pops against the map.
        return hoveredBuildingId === buildingId ? [base[0], base[1], base[2], 255] : base;
    }

    return new GeoJsonLayer({
        id: 'buildings-layer',
        filled: true,
        getFillColor: (object) => getFillColor(object.properties.id as string),
        updateTriggers: {
            getFillColor: [selectedBuildings, realEstateAssets, hoveredBuildingId],
        },
        getLineWidth: 0.5,
        data: geoJsonFeatures,
        pickable: true,

        onHover: (info) => {
            const newId = (info.object?.properties?.id as string | undefined) ?? null;
            onHoverChanged?.(newId);
        },

        onClick: (info) => {
            if (!info.object) return;
            const buildingId = info.object.properties.id as string;
            const clicked = buildings.find((b) => b.id === buildingId);
            if (!clicked) return;

            if (onBuildingClicked) {
                onBuildingClicked(clicked);
                return;
            }

            const newSelected = isBuildingSelected(buildingId)
                ? internalSelected.filter((b) => b.id !== buildingId)
                : [...internalSelected, clicked];

            setInternalSelected(newSelected);
            onSelectionChanged?.(newSelected);
        },
    });
}
